import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientsService } from '@/services/clientsService';
import type { CreateClientData, UpdateClientData } from '@/services/clientsService';

const QUERY_KEY = 'clients';

export const useClientsQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => clientsService.getClients(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useClientQuery = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => clientsService.getClientById(id),
    enabled: !!id,
  });
};

export const useCreateClientMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateClientData) => clientsService.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateClientMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateClientData) => clientsService.updateClient(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
};

export const useDeleteClientMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => clientsService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};